import { Component, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../auth/auth.service';
import { DashboardType, EntryDictionary } from '@generativ/ms-student-types';
import { ContentfulService } from '../../shared/contentful.service';
import { PaymentService } from '../auth/payment.service';
import { Subscription } from 'rxjs';
import { FeedbackService } from '../feedback/feedback.service';
import { CourseService } from '../../shared/course.service';
import { CourseModel } from '@generativ/much-smarter-models';
import { Router, RouterLink } from '@angular/router';
import { NgClass, UpperCasePipe } from '@angular/common';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { MarkdownModule } from 'ngx-markdown';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterLink, NgClass, SpinnerComponent, UpperCasePipe, MarkdownModule]
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild('modal', { static: true }) private modal;
  // @ViewChild('upgrade') private upgradeModal;
  subscriptions: Subscription = new Subscription();
  content: DashboardType;
  introVideo: string;
  upgrading: boolean;
  allCourses: CourseModel[] = [];
  ukCourses: CourseModel[] = [];
  usCourses: CourseModel[] = [];
  activeCourse = null;
  coursesHidden = true;
  @ViewChild('coursesDropdown') private coursesDropdown;

  constructor(private authService: AuthService,
              private modalService: NgbModal,
              private contentfulService: ContentfulService,
              public paymentService: PaymentService,
              private feedbackService: FeedbackService,
              private courseService: CourseService,
              private router: Router
  ) {
  }

  ngOnInit() {
    this.contentfulService.getStudentEntry(EntryDictionary.Dashboard.dashboard).then(
      (entry) => {
        this.content = new DashboardType(entry);
      }
    );

    this.subscriptions.add(this.courseService.courses.subscribe((courses: CourseModel[]) => {
      this.allCourses = courses;
      // Filter courses shown by country
      this.filterCoursesByCountry();
      // If we have have course list assign active course
      if (courses.length > 0) {
        this.checkForActiveCourse();
      }
    }));

    this.subscriptions.add(this.courseService.activeCourse.subscribe((course: CourseModel) => {
      this.activeCourse = course;
    }));

    // Fetch new courses if we haven't fetched before
    if (this.courseService.courses.getValue().length === 0) {
      this.courseService.fetchAllCourses();
    }
    this.getIntroVideoWatched();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('window:mouseup', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.coursesDropdown.nativeElement.contains(event.target)) {
      this.coursesHidden = true;
    }
  }

  filterCoursesByCountry() {
    for (const course of this.allCourses) {
      if (course.country === 'uk') {
        this.ukCourses.push(course);
      }
      if (course.country === 'us') {
        this.usCourses.push(course);
      }
    }
  }

  checkForActiveCourse() {
    const selectedCourseId = +localStorage.getItem('selectedCourse');
    const activeCountry = this.courseService.getCurrentCountry();
    for (const course of this.allCourses) {
      if (course.country === activeCountry && course.id === selectedCourseId) {
        // If selected selected course exists in selected country set it up
        this.courseService.setActiveCourse(selectedCourseId);
        return;
      }
    }
    // If couldn't find course in existing one return back to course selection page
    this.navigateBackToCourseSelection();
  }

  getActiveCourseList(): CourseModel[] {
    const currentCountry = this.courseService.getCurrentCountry();
    return currentCountry === 'us' ? this.usCourses : this.ukCourses;
  }

  navigateBackToCourseSelection() {
    // if (localStorage.getItem('selectedCountry')){
    //   localStorage.removeItem('selectedCountry');
    // }
    if (localStorage.getItem('selectedCourse')) {
      localStorage.removeItem('selectedCourse');
    }
    this.router.navigate(['/login/location-and-game']);
  }

  canSwitchCountry(): boolean {
    const currentCountry = this.courseService.getCurrentCountry();
    const oppositeCountryCourses: CourseModel[] = currentCountry === 'us' ? this.ukCourses : this.usCourses;
    return oppositeCountryCourses.length > 0;
  }

  switchCountry() {
    this.coursesHidden = true;
    const newCountry = this.getNewCountry();
    const token = localStorage.getItem('tokenId');
    this.courseService.setCurrentCountry(newCountry);
    // When switching countris set first course of other country as selected course
    localStorage.removeItem('selectedCourse');
    const url = newCountry === 'us'
      ? `https://games.muchsmarter.com?tokenId=${token}`
      : `https://games.muchsmarter.co.uk?tokenId=${token}`;
    console.log(url);
    window.location.href = url;

    // window.location.reload();
  }

  getNewCountry(): string {
    const currentCountry = this.courseService.getCurrentCountry();
    return currentCountry === 'uk' ? 'us' : 'uk';
  }

  selectCourse(courseId: number) {
    this.courseService.setActiveCourse(courseId);
    this.coursesHidden = true;
    this.router.navigate(['/']);
  }

  getIntroVideoWatched() {
    this.introVideo = localStorage.getItem('introVideo');

    if (this.introVideo !== 'true') {
      localStorage.setItem('introVideo', 'true');
      this.modalService.open(this.modal, {
        size: 'lg',
        windowClass: 'welcome-video-modal',
        backdropClass: 'welcome-video-backdrop'
      });
    }
  }

  logOut() {
    this.authService.logout();
  }

  open(modal) {
    this.modalService.open(modal, {
      size: 'lg',
      windowClass: 'welcome-video-modal',
      backdropClass: 'welcome-video-backdrop'
    });
  }

  upgradeToPremium(): void {
    this.upgrading = true;
    this.subscriptions.add(
      this.paymentService.upgradeToPremium()
        .subscribe(() => {
          this.paymentService.status.premium = true;
          this.feedbackService.success('Subscription upgraded!', true);
          setTimeout(() => {
            this.modalService.dismissAll();
            this.upgrading = false;
          }, 3000);
        }, error => {
          console.log(error);
        })
    );
  }
}
